import * as stylex from '@stylexjs/stylex';
import { useInfiniteQuery, useMutation, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useNumberFormatter } from 'react-aria';


import { ArticleListItem } from '@/components/article-list-item.component';
import { ArticleList } from '@/components/article-list.component';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger.hook';
import { Heading, ProgressIndicator, Text } from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { articleMutations, articleQueries } from '@/resources/article/article.api';

export const Route = createFileRoute('/_app/_list/saved')({
  component: RouteComponent,
  staticData: {
    contextBarComponent: ContextBarComponent,
  },
  loader: ({ context }) => context.queryClient.ensureInfiniteQueryData(articleQueries.saved),
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Saved',
      },
    ],
  })
});

function RouteComponent() {
  const unsaveArticleMutation = useMutation(articleMutations.unsave());
  const excludeArticleMutation = useMutation(articleMutations.exclude());
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude());

  const {
    data,
    fetchNextPage,
    isFetching,
    isLoading,
    hasNextPage
  } = useSuspenseInfiniteQuery(articleQueries.saved);

  useInfiniteScrollTrigger({ isFetching, hasNextPage, fetchNextPage });

  const items = data?.pages?.flatMap((page) => page.articles) ?? [];

  return (
    <>
      <ArticleList aria-label="Saved articles list" items={items}>
        {({ key, index, style, ref, ...item }) => (
          <ArticleListItem
            authors={item.authors}
            data-index={index}
            id={item.id}
            isExcluded={item.excluded}
            isLast={index === items.length - 1}
            isSaved={item.saved}
            isSeen={item.seen}
            key={key}
            publicationDate={item.publication_date}
            publicationId={item.publication_id}
            publicationName={item.publication_name}
            ref={ref}
            style={style}
            title={item.title}
            url={item.url}
            onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
            onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
            onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
          />)}
      </ArticleList>
      {!isLoading && hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
    </>
  );
}

function ContextBarComponent() {
  const numberFormatter = useNumberFormatter();

  const savedArticlesQuery = useInfiniteQuery(articleQueries.saved);

  const total = savedArticlesQuery.data?.pages[0]?.total ? numberFormatter.format(savedArticlesQuery.data?.pages[0]?.total) : '';

  return (
    <div {...stylex.props(contextBarStyles.base)}>
      <Heading level={1} size={24}>Saved</Heading>
      <Text color="placeholder" size={14}>{total}</Text>
    </div>
  );
}

const contextBarStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginLeft: {
      default: 0,
      [breakpoints.large]: 'auto',
    },
    marginRight: {
      default: 0,
      [breakpoints.large]: 'auto',
    },
    paddingBlock: 12,
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
    width: {
      default: '100%',
      [breakpoints.large]: 728,
    },
  },
});

const progressIndicatorStyles = stylex.create({
  base: {
    marginBottom: 20,
    marginTop: 56,
  }
});
